import { create } from "zustand";
import type {
  CanvasTool,
  CanvasObjectProperties,
  LayerEntry,
} from "./components/CanvasPanel.logic";
import type { BrandKit } from "./components/canvas/canvasGenerator";
import { DEFAULT_BRAND_KIT } from "./components/canvas/canvasGenerator";

const MAX_HISTORY = 50;
const PERSISTED_STATE_KEY = "forge:canvas-state:v1";

interface SavedDesign {
  json: string;
  name: string;
  updatedAt: string;
}

interface CanvasState {
  activeTool: CanvasTool;
  artboardPresetId: string | null;
  artboardWidth: number;
  artboardHeight: number;
  selectedObjectIds: string[];
  selectedObjectProperties: CanvasObjectProperties | null;
  propertiesPanelOpen: boolean;
  historyStack: string[];
  historyIndex: number;
  layers: LayerEntry[];
  zoomLevel: number;
  savedDesigns: Record<string, SavedDesign>;
  brandKit: BrandKit;
  /** Set by ChatView when user sends a design-related message while canvas is open. */
  pendingGeneratePrompt: string | null;
}

interface CanvasActions {
  setActiveTool: (tool: CanvasTool) => void;
  setArtboard: (presetId: string | null, width: number, height: number) => void;
  setSelectedObjects: (ids: string[], properties: CanvasObjectProperties | null) => void;
  clearSelection: () => void;
  setPropertiesPanelOpen: (open: boolean) => void;
  pushHistorySnapshot: (json: string) => void;
  undo: () => string | null;
  redo: () => string | null;
  setLayers: (layers: LayerEntry[]) => void;
  setZoomLevel: (zoom: number) => void;
  saveDesign: (id: string, name: string, json: string) => void;
  loadDesign: (id: string) => string | null;
  deleteDesign: (id: string) => void;
  resetHistory: () => void;
  setBrandKit: (kit: BrandKit) => void;
  updateBrandKit: (partial: Partial<BrandKit>) => void;
  requestCanvasGenerate: (prompt: string) => void;
  consumePendingGenerate: () => string | null;
}

function loadPersistedState(): Partial<CanvasState> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(PERSISTED_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<CanvasState>;
    const result: Partial<CanvasState> = {
      savedDesigns: parsed.savedDesigns ?? {},
      artboardPresetId: parsed.artboardPresetId ?? null,
      activeTool: parsed.activeTool ?? "select",
    };
    if (parsed.brandKit) result.brandKit = parsed.brandKit;
    return result;
  } catch {
    return {};
  }
}

function persistState(state: CanvasState): void {
  if (typeof window === "undefined") return;
  try {
    const persisted = {
      savedDesigns: state.savedDesigns,
      artboardPresetId: state.artboardPresetId,
      activeTool: state.activeTool,
      brandKit: state.brandKit,
    };
    localStorage.setItem(PERSISTED_STATE_KEY, JSON.stringify(persisted));
  } catch {
    // localStorage full — silently ignore
  }
}

const persisted = loadPersistedState();

const initialState: CanvasState = {
  activeTool: persisted.activeTool ?? "select",
  artboardPresetId: persisted.artboardPresetId ?? "instagram-post",
  artboardWidth: 1080,
  artboardHeight: 1080,
  selectedObjectIds: [],
  selectedObjectProperties: null,
  propertiesPanelOpen: false,
  historyStack: [],
  historyIndex: -1,
  layers: [],
  zoomLevel: 1,
  savedDesigns: persisted.savedDesigns ?? {},
  brandKit: persisted.brandKit ?? DEFAULT_BRAND_KIT,
  pendingGeneratePrompt: null,
};

export const useCanvasStore = create<CanvasState & CanvasActions>()((set, get) => ({
  ...initialState,

  setActiveTool: (tool) => {
    set({ activeTool: tool });
    persistState(get());
  },

  setArtboard: (presetId, width, height) => {
    set({ artboardPresetId: presetId, artboardWidth: width, artboardHeight: height });
    persistState(get());
  },

  setSelectedObjects: (ids, properties) => {
    set({
      selectedObjectIds: ids,
      selectedObjectProperties: properties,
      propertiesPanelOpen: ids.length > 0,
    });
  },

  clearSelection: () => {
    set({
      selectedObjectIds: [],
      selectedObjectProperties: null,
    });
  },

  setPropertiesPanelOpen: (open) => {
    set({ propertiesPanelOpen: open });
  },

  pushHistorySnapshot: (json) => {
    set((state) => {
      const truncatedStack = state.historyStack.slice(0, state.historyIndex + 1);
      const newStack = [...truncatedStack, json].slice(-MAX_HISTORY);
      return {
        historyStack: newStack,
        historyIndex: newStack.length - 1,
      };
    });
  },

  undo: () => {
    const state = get();
    if (state.historyIndex <= 0) return null;
    const newIndex = state.historyIndex - 1;
    set({ historyIndex: newIndex });
    return state.historyStack[newIndex] ?? null;
  },

  redo: () => {
    const state = get();
    if (state.historyIndex >= state.historyStack.length - 1) return null;
    const newIndex = state.historyIndex + 1;
    set({ historyIndex: newIndex });
    return state.historyStack[newIndex] ?? null;
  },

  setLayers: (layers) => {
    set({ layers });
  },

  setZoomLevel: (zoom) => {
    set({ zoomLevel: zoom });
  },

  saveDesign: (id, name, json) => {
    set((state) => ({
      savedDesigns: {
        ...state.savedDesigns,
        [id]: { json, name, updatedAt: new Date().toISOString() },
      },
    }));
    persistState(get());
  },

  loadDesign: (id) => {
    const state = get();
    return state.savedDesigns[id]?.json ?? null;
  },

  deleteDesign: (id) => {
    set((state) => {
      const { [id]: _, ...rest } = state.savedDesigns;
      return { savedDesigns: rest };
    });
    persistState(get());
  },

  resetHistory: () => {
    set({ historyStack: [], historyIndex: -1 });
  },

  setBrandKit: (kit) => {
    set({ brandKit: kit });
    persistState(get());
  },

  updateBrandKit: (partial) => {
    set((state) => ({ brandKit: { ...state.brandKit, ...partial } }));
    persistState(get());
  },

  requestCanvasGenerate: (prompt) => {
    set({ pendingGeneratePrompt: prompt });
  },

  consumePendingGenerate: () => {
    const prompt = get().pendingGeneratePrompt;
    if (prompt) set({ pendingGeneratePrompt: null });
    return prompt;
  },
}));
